// Which language a staff screen reads in before anybody has signed in, and the
// browser's memory of the last one used.
//
// After sign-in the answer is the server's (identity.language). Before it, the
// order is: what this browser last showed, because the person at this desk
// yesterday is likely the person at it today; the organisation's own, when the
// server has exactly one; the browser's language, when it is one of the three;
// English.
import { isStaffLanguage, type StaffLanguage } from './staffText'

const KEY = 'qms.staffLanguage'

export function signInLanguage(
  remembered: string | null,
  organisations: readonly { language?: string }[],
  browser: readonly string[],
): StaffLanguage {
  if (isStaffLanguage(remembered)) return remembered
  const only = organisations.length === 1 ? organisations[0]?.language : undefined
  if (isStaffLanguage(only)) return only
  for (const tag of browser) {
    const base = tag.toLowerCase().split('-')[0]
    if (isStaffLanguage(base)) return base
  }
  return 'en'
}

/** The language last shown on this browser, if storage is there to ask. */
export function rememberedLanguage(): StaffLanguage | null {
  try {
    const value = window.localStorage.getItem(KEY)
    return isStaffLanguage(value) ? value : null
  } catch {
    return null
  }
}

export function rememberLanguage(lang: StaffLanguage): void {
  try {
    window.localStorage.setItem(KEY, lang)
  } catch {
    // A private window, or storage switched off: the next sign-in screen falls
    // back to the organisation's or the browser's, which is still readable.
  }
}

/**
 * The language to save to a person as they sign in, or null for none.
 *
 * Only one pressed on the sign-in screen, on this visit, is kept: the screen's
 * own guess — this browser's memory, the organisation's, the browser's — says
 * nothing about the person who then signs in, and writing it to their account
 * would stop their administrator's default from reaching them without anybody
 * having chosen that. And only for a person with no language of their own, whose
 * screens would otherwise switch to their organisation's the moment they are in:
 * a choice they made on the Account screen is not overridden by a press on a
 * shared desk's sign-in screen. A press for the language they would read anyway
 * saves nothing, so they go on following their organisation.
 */
export function languageToKeep(
  pressed: StaffLanguage | null,
  signedIn: { language: string; staff: { language?: string } },
): StaffLanguage | null {
  if (!pressed) return null
  if (signedIn.staff.language) return null
  return pressed === signedIn.language ? null : pressed
}
